import { useState } from 'react';
import { api } from '../lib/api';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import FormError from '../components/FormError';
import { useBranding } from '../lib/branding';

const demoSections = [
  {
    title: 'Back office - classic web',
    description: 'Operations, compliance reviews, account controls and admin workflows.',
    accounts: [
      { role: 'admin', label: 'Super Admin', area: 'Back office', surface: 'web' },
      { role: 'compliance', label: 'Compliance', area: 'Reviews', surface: 'web' },
      { role: 'manager', label: 'Manager', area: 'Operations', surface: 'web' },
    ],
  },
  {
    title: 'Client portal - React web app',
    description: 'Modern React experience served under /app for client journeys.',
    accounts: [
      { role: 'client', label: 'Business Client', area: 'React /app', surface: 'app' },
      { role: 'personal', label: 'Personal Client', area: 'React /app', surface: 'app' },
    ],
  },
  {
    title: 'Client portal - classic web',
    description: 'Server-rendered web dashboard for the same business and personal users.',
    accounts: [
      { role: 'client', label: 'Business Client', area: 'Web /dashboard', surface: 'web' },
      { role: 'personal', label: 'Personal Client', area: 'Web /dashboard', surface: 'web' },
    ],
  },
];

export default function Login() {
  const branding = useBranding();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const redirectTo = params.get('redirect') || '/';
  const showDemoAccess =
    params.has('demo') ||
    (typeof window !== 'undefined' && window.location.hostname.startsWith('demo.'));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setErr(null);
    try {
      const response = await api.post<{ redirect?: string }>('/xapi/auth/login', { login, password, remember });
      const next = redirectTo === '/' && response?.redirect ? response.redirect : redirectTo;
      nav(next, { replace: true });
    } catch (error: any) {
      if (error?.status === 422) {
        setErr('Invalid credentials.');
      } else if (error?.status === 429) {
        setErr('Too many attempts. Please try again shortly.');
      } else if (error?.status === 419) {
        setErr('Session expired. Please try again.');
      } else {
        setErr(error?.message || 'Login failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-4 bg-gray-50">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow p-6 text-center">
        {/* Centered product logo */}
        <img
          src={branding.theme.logo_square_url}
          alt={branding.product_name}
          className="mx-auto mb-4 h-16 w-16 object-contain"
        />

        <h1 className="text-2xl font-semibold mb-2">Sign in</h1>

        {err && <FormError message={err} />}

        {showDemoAccess && (
          <div className="mb-5 rounded-xl border border-slate-200 bg-slate-50 p-3 text-left">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Demo access</p>
                <p className="mt-0.5 text-xs leading-5 text-slate-600">
                  Pick the product surface you want to inspect.
                </p>
              </div>
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
                Safe sandbox
              </span>
            </div>
            <div className="space-y-3">
              {demoSections.map(section => (
                <section key={section.title} className="rounded-lg border border-slate-200 bg-white p-2.5">
                  <div className="mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                      {section.title}
                    </p>
                    <p className="mt-0.5 text-xs leading-5 text-slate-500">{section.description}</p>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    {section.accounts.map(account => (
                      <a
                        key={`${account.role}-${account.surface}`}
                        href={`/demo-login/${account.role}/${account.surface}`}
                        className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm transition hover:border-blue-300 hover:bg-blue-50"
                      >
                        <span className="font-medium text-slate-900">{account.label}</span>
                        <span className="shrink-0 text-xs font-medium text-slate-500">{account.area}</span>
                      </a>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4 text-left">
          <div>
            <label className="block text-sm font-medium">Email or Username</label>
            <input
              type="text"
              autoComplete="username"
              className="mt-1 w-full rounded-xl border px-3 py-2"
              value={login}
              onChange={e => setLogin(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Password</label>
            <input
              type="password"
              autoComplete="current-password"
              className="mt-1 w-full rounded-xl border px-3 py-2"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
            />
          </div>

          <div className="flex items-center justify-between">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={remember}
                onChange={e => setRemember(e.target.checked)}
              />
              Remember me
            </label>
            <Link className="text-sm underline" to="/forgot-password">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-xl bg-blue-600 text-white py-2 font-medium disabled:opacity-60"
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        {/* ✅ Clear split between personal and business */}
        <div className="mt-6 space-y-2">
          <p className="text-sm text-gray-600">Don’t have access?</p>
          <div className="grid grid-cols-1 gap-2">
            <Link
              className="w-full inline-flex items-center justify-center rounded-xl border border-gray-300 py-2 text-sm hover:bg-gray-50"
              to="/register/personal"
            >
              Create personal profile
            </Link>
            <Link
              className="w-full inline-flex items-center justify-center rounded-xl border border-gray-300 py-2 text-sm hover:bg-gray-50"
              to="/register/business"
            >
              Create business profile
            </Link>
          </div>        
        </div>
      </div>
    </div>
  );
}
